<ng-container *transloco="let t">
  <div id="migration-login-card" class="bg-white shadow-card w-full max-w-[440px] min-w-0 p-4 sm:p-10">
    @if (showBackButton()) {
      <div id="migration-back-header" class="flex items-center gap-3 mb-4">
        <button
          type="button"
          id="migration-back-arrow-btn"
          (click)="onBackToLogin()"
          class="flex items-center justify-center w-10 h-10 rounded-full bg-transparent border-0 cursor-pointer hover:bg-ld-neutral-100 transition-colors focus-visible:outline-2 focus-visible:outline-ld-primary"
          [attr.aria-label]="t('commonLabelsBackToSignIn')"
        >
          <mat-icon class="w-6! h-6! text-[24px]! text-ld-neutral-500">arrow_back</mat-icon>
        </button>
        <h1 id="migration-login-title-mobile" class="text-xl font-semibold text-ld-neutral-700 m-0">
          {{ t('commonLabelsSignIn') }}
        </h1>
      </div>
    } @else {
      <div id="migration-login-header" class="flex flex-col items-center gap-2 mb-6">
        <img id="migration-login-logo" [src]="logoUrl()" [alt]="partnerDisplayName()" class="h-12 w-auto" />
        <h1 id="migration-login-title" class="text-xl font-semibold text-ld-neutral-700 m-0">
          {{ t('commonLabelsSignIn') }}
        </h1>
      </div>
    }

    <div id="migration-notice" class="text-center mb-6">
      <h2 id="migration-notice-subtitle" class="font-dm font-semibold text-base leading-6 text-ld-link m-0 mb-3">
        {{ t('authMigrationLoginSubtitle') }}
      </h2>
      <p id="migration-notice-description" class="font-dm text-sm leading-5 text-ld-neutral-500 m-0 mb-3">
        {{ t('authMigrationLoginDescription', { companyName: partnerDisplayName() }) }}
      </p>
      <p id="migration-notice-prompt" class="font-dm text-sm leading-5 text-ld-neutral-500 m-0">
        {{ t('authMigrationLoginPrompt') }}
      </p>
    </div>

    @if (errorMessage()) {
      <div
        id="migration-login-error-message"
        class="bg-ld-error-light border border-ld-error rounded-lg px-4 py-3 mb-6 text-ld-error text-sm"
        role="alert"
      >
        {{ errorMessage() }}
      </div>
    }

    <form id="migration-login-form" [formGroup]="migrationForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-5">
      <div id="migration-email-group" class="flex flex-col">
        <label id="migration-email-label" for="migration-email-input" class="text-sm font-dm text-ld-neutral-700 mb-1">
          {{ t('commonFieldsEmail') }}
        </label>
        <input
          id="migration-email-input"
          type="email"
          formControlName="email"
          aria-required="true"
          class="w-full border-0 border-b-2 border-ld-neutral-300 bg-transparent px-0 py-2 text-base font-dm text-ld-neutral-700 outline-none transition-colors focus:border-ld-primary placeholder:text-ld-neutral-400"
          [placeholder]="t('authMigrationEmailPlaceholder')"
          (keydown.enter)="onEnterKey($event)"
        />
        @if (migrationForm.get('email')?.touched && migrationForm.get('email')?.hasError('required')) {
          <span id="migration-email-error-required" class="text-xs text-ld-error mt-1" role="alert">
            {{ t('commonValidationEmailRequired') }}
          </span>
        } @else if (migrationForm.get('email')?.touched && migrationForm.get('email')?.hasError('nativeEmailError')) {
          <span id="migration-email-error-native" class="text-xs text-ld-error mt-1" role="alert">
            {{ t('commonValidationInvalidEmail') }}
          </span>
        }
      </div>

      <div id="migration-password-group" class="flex flex-col">
        <label
          id="migration-password-label"
          for="migration-password-input"
          class="text-sm font-dm text-ld-neutral-700 mb-1"
        >
          {{ t('commonFieldsPassword') }}
        </label>
        <div class="relative">
          <input
            id="migration-password-input"
            [type]="hidePassword() ? 'password' : 'text'"
            formControlName="password"
            aria-required="true"
            glance_masked="true"
            class="w-full border-0 border-b-2 border-ld-neutral-300 bg-transparent px-0 py-2 pr-10 text-base font-dm text-ld-neutral-700 outline-none transition-colors focus:border-ld-primary placeholder:text-ld-neutral-400"
            [placeholder]="t('authMigrationPasswordPlaceholder')"
            (keydown.enter)="onEnterKey($event)"
          />
          <button
            type="button"
            id="migration-password-toggle-button"
            (click)="togglePasswordVisibility()"
            class="absolute right-0 top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer p-1 text-ld-neutral-400 hover:text-ld-neutral-700"
            [attr.aria-label]="hidePassword() ? t('commonAriaShowPassword') : t('commonAriaHidePassword')"
          >
            <mat-icon class="text-[20px]!">{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </div>
        @if (migrationForm.get('password')?.touched && migrationForm.get('password')?.hasError('required')) {
          <span id="migration-password-error-required" class="text-xs text-ld-error mt-1" role="alert">
            {{ t('commonValidationPasswordRequired') }}
          </span>
        }
      </div>

      <div id="migration-helper-links" class="flex items-center justify-start">
        <button
          type="button"
          id="migration-forgot-password-link"
          (click)="onForgotPasswordClick()"
          class="bg-transparent border-0 text-ld-entra cursor-pointer p-0 font-dm text-sm font-medium leading-4 hover:underline focus-visible:outline-2 focus-visible:outline-ld-entra focus-visible:outline-offset-2 focus-visible:rounded touch-target"
        >
          {{ t('authLoginForgotPassword') }}
        </button>
      </div>

      <button
        type="submit"
        id="migration-login-button"
        class="w-full h-11 rounded bg-ld-entra text-white border-0 font-dm font-semibold text-base cursor-pointer transition-all hover:bg-ld-entra-hover active:scale-[0.98] disabled:bg-ld-entra/60 disabled:cursor-wait focus-visible:outline-2 focus-visible:outline-ld-entra focus-visible:outline-offset-2 flex items-center justify-center gap-2"
        [disabled]="loading()"
        [attr.aria-busy]="loading()"
      >
        @if (loading()) {
          <mat-spinner
            diameter="20"
            id="migration-login-spinner"
            class="text-white! [&_circle]:stroke-white!"
          ></mat-spinner>
          <span>{{ t('authLoginSigningIn') }}</span>
        } @else {
          <span>{{ t('commonLabelsSignIn') }}</span>
        }
      </button>
    </form>
  </div>
</ng-container>
